import { MessageServiceActions } from "@common/services/messageServiceActions";

/**
 * Notify the background chrome.storage hub of a logout so every hub-syncing surface
 * (the popup, options page, etc.) logs out too. This is the logout counterpart to
 * persistToken's login bridge.
 *
 *  - extension/content-script world (chrome.runtime.id present): message the
 *    background directly with `logout`.
 *  - web-app page world (chrome.runtime exists via externally_connectable but
 *    chrome.runtime.id is undefined): post a `web-logout` window message for the
 *    co-injected content script / attribution-bridge to relay to the background as
 *    `logout`. A cmd distinct from `logout` so it doesn't collide with the content
 *    script's wa-state-manager `logout` handler.
 *
 * Both logout call sites — AuthService.clearStoredToken (WhatsApp-Web / programmatic
 * logout) and logoutWrapperThunk (the UI Log Out path) — MUST route their hub
 * notification through here so the transport stays consistent across surfaces.
 *
 * @param opts.skipDirectBackgroundMessage  When true, do NOT send the direct
 *   background "logout" in the extension/content world — only bridge the web page
 *   world. logoutWrapperThunk sets this because its dispatched logoutThunk already
 *   messages the background "logout" in that context; without it the background would
 *   receive a redundant second "logout" per UI logout.
 */
export function notifyBackgroundOfLogout(opts?: { skipDirectBackgroundMessage?: boolean }): void {
  const canMessageBackground = typeof chrome !== "undefined" && !!chrome.runtime?.id;
  console.log("[login-flow] notifyBackgroundOfLogout: entry", { canMessageBackground, skipDirect: !!opts?.skipDirectBackgroundMessage });
  if (canMessageBackground) {
    if (opts?.skipDirectBackgroundMessage) {
      console.log('[login-flow] notifyBackgroundOfLogout: extension context already handled by caller; skipping direct background "logout"');
      return;
    }
    console.log('[login-flow] notifyBackgroundOfLogout: branch=runtime-id direct -> background "logout"');
    MessageServiceActions.postMessageToBackgroundWithResponse<string>("logout", {});
  } else if (typeof window !== "undefined") {
    // Target THIS origin only (never '*'); the co-injected content script shares it.
    console.log("[login-flow] notifyBackgroundOfLogout: branch=window.postMessage web-logout");
    const targetOrigin = typeof window.location !== "undefined" && window.location?.origin ? window.location.origin : "/";
    window.postMessage({ cmd: "web-logout" }, targetOrigin);
  } else {
    console.log("[login-flow] notifyBackgroundOfLogout: branch=no transport");
  }
}
